import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { billingAPI } from '../services/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling } from '../contexts/BillingContext';
import { useToast } from '../contexts/ToastContext';
import { APP_CONFIG } from '../config/appConfig';
import { parseServerIso } from '../utils/datetime';

// Renders inline inside a sentence, so an unparseable date collapses to ''
// rather than the NO_DATE placeholder used in table cells. Mirrors
// PendingPlanBanner deliberately — the two banners must read the same.
// 🚨 Returns null, NOT '' and NOT NO_DATE ('—'), for a missing or
// unparseable date. `effective_at` CAN be null: the backend deliberately does
// not raise when Stripe returns no period at cancel time, because a 500 would
// tell the customer their cancellation failed when it had actually succeeded.
// An empty string here produced "Your plan ends on . You keep everything
// until then." — a broken sentence — and NO_DATE would have produced a bare
// em dash. Null forces the caller to change the SENTENCE instead.
const formatDateOrNull = (iso?: string | null): string | null => {
  const d = parseServerIso(iso);
  if (!d) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PERIOD_END_PHRASE = 'the end of your current billing period';

// Pending-cancellation strip. Sibling of PendingPlanBanner, and mutually
// exclusive with it server-side: scheduling a cancellation releases any
// pending plan change, so the two never show at once.
//
// Red rather than amber on purpose — a pending downgrade keeps the customer
// on a paid plan, a pending cancellation ends the subscription outright.
//
// Stripe build only. The Shopify view owns its own cancellation flow and
// never stamps `pending_cancellation`; gating on the provider keeps a
// future Shopify payload from lighting up a banner whose "Keep my plan"
// button posts to a Stripe route.
const PendingCancellationBanner: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const { billing, refetch } = useBilling();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [resuming, setResuming] = useState(false);

  const pending = billing?.pending_cancellation;
  if (APP_CONFIG.billingProvider === 'shopify') return null;
  if (!pending || dismissed || !activeWorkspace) return null;

  const endDate = formatDateOrNull(pending.effective_at);

  const handleResume = async () => {
    setResuming(true);
    try {
      await billingAPI.resumeSubscription(activeWorkspace.id);
      showToast('Your plan is back on. Nothing will be cancelled.', 'success');
      await refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        showToast(detail || 'This plan is not scheduled to cancel.', 'info');
        await refetch();
      } else if (status === 403) {
        showToast(
          detail || 'Only the workspace owner can change the plan.',
          'error',
        );
      } else {
        showToast(
          detail ||
            'Could not restore your plan. Please try again or contact support.',
          'error',
        );
      }
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-rose-500 to-red-600 text-white px-4 py-3 shadow-md">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center space-x-3 min-w-0">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-sm font-medium leading-snug">
            {endDate ? (
              <>
                Your plan ends on <span className="font-bold">{endDate}</span>
              </>
            ) : (
              <>Your plan ends at {PERIOD_END_PHRASE}</>
            )}
            . You keep everything until then.
            {pending.liveavatar_will_cancel
              ? ' Your AI Avatar add-on ends on the same day.'
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/billing')}
            className="bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
          >
            View details
          </button>
          <button
            onClick={handleResume}
            disabled={resuming}
            className="bg-white text-red-600 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {resuming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Keep my plan
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-white/70 hover:text-white p-1 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingCancellationBanner;
